import { useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ModernWebLayout, MainContent } from "../../components/Layout";
import TableOfContentsSideMenu from "../../components/TableOfContents/TableOfContentsSideMenu";
import ModernMainContent from "./ModernMainContent";
import { useTableOfContentsScroll } from "../../hooks/tableOfContentsHooks/useTableOfContentsScroll";
import AppTopicBanner from "../../components/AppTopicBanner/AppTopicBanner";
import useScreenSize from "../../hooks/useScreenSize";
import { useFindInPageHighlighter } from "../../hooks/FindInPageHooks/useFindInPageHighlighter";
import { setIsAppMode, setIsModernView } from "../counter/counterSlice";
import useAppModernisation from "../../hooks/useAppModernisation";
import { useDispatch } from "react-redux";
import { useOpenInModalRender } from "../../hooks/OpenInModalHooks/useOpenInModalRender";
import TopicActionBar from "../../components/TopicActionBar/TopicActionBar";
import useMobileOrientation from "../../hooks/useMobileOrientation";
import ExpertGroupPopup from "../../components/ExpertGroups/ExpertGroupPopup";
import UpdatesPopup from "../../components/Content/UpdatesPopup";
import { useFontResize } from "../../hooks/fontSizeAdjustorHooks/useFontResize";
import { ScrollProvider } from "../../contexts/ScrollContext";
import GoToTopButton from "../../components/GoToTopButton/GoToTopButton";

const ModernApp = () => {
  const { pathname, search, hash } = useLocation();
  const contentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  useTableOfContentsScroll(contentRef);
  useFindInPageHighlighter(contentRef);
  useOpenInModalRender(contentRef);
  useFontResize(contentRef);
  const { isPortrait } = useMobileOrientation();
  const { isMobileView, isModernView } = useAppModernisation();
  const { isDesktop, isTabletSize } = useScreenSize();
  const dispatch = useDispatch();
  const [isExpertGroupsExpanded, setIsExpertGroupsExpanded] = useState(false);
  const [isUpdatesExpanded, setIsUpdatesExpanded] = useState(false);
  const [showTopicActionBar, setShowTopicActionBar] = useState(true);

  const expandExpertGroupsSideBar = () => {
    setIsExpertGroupsExpanded(!isExpertGroupsExpanded);
  };
  const expandUpdatesSideBar = () => {
    setIsUpdatesExpanded(!isUpdatesExpanded);
  };

  useEffect(() => {
    dispatch(setIsAppMode(isMobileView));
    dispatch(setIsModernView(isModernView));
  }, [isMobileView, isModernView, dispatch]);

  // Close side popups whenever the route changes (e.g. user clicks an xref
  // that navigates to a different topic/guideline).
  useEffect(() => {
    setIsExpertGroupsExpanded(false);
    setIsUpdatesExpanded(false);
  }, [pathname, search, hash]);

  return (
    <>
      <ScrollProvider scrollContainerRef={scrollContainerRef}>
        <ModernWebLayout
          title="Guidelines - Therapeutic Guidelines"
          stickyPageSidebar={<TableOfContentsSideMenu msPath={`${search}${hash ? hash : ""}`} />}
          stickyPageHeader={
            (!isDesktop || isTabletSize) && (
              <AppTopicBanner
                pathname={pathname}
                search={search}
                hash={hash}
                expandExpertGroupsSideBar={expandExpertGroupsSideBar}
                expandUpdatesSideBar={expandUpdatesSideBar}
                setShowTopicActionBar={setShowTopicActionBar}
              />
            )
          }
          showSidebar={isDesktop}
        >
          <MainContent padding="none" className={"webMode"}>
            {isDesktop && !isTabletSize && (
              <AppTopicBanner
                pathname={pathname}
                search={search}
                hash={hash}
                expandExpertGroupsSideBar={expandExpertGroupsSideBar}
                expandUpdatesSideBar={expandUpdatesSideBar}
                setShowTopicActionBar={setShowTopicActionBar}
              />
            )}
            <ModernMainContent
              ref={contentRef}
              scrollContainerRef={scrollContainerRef}
              expandExpertGroupsSideBar={expandExpertGroupsSideBar}
              expandUpdatesSideBar={expandUpdatesSideBar}
            />
          </MainContent>
        </ModernWebLayout>
        <GoToTopButton />
      </ScrollProvider>
      {isMobileView && isPortrait && showTopicActionBar && <TopicActionBar />}
      <ExpertGroupPopup
        isExpanded={isExpertGroupsExpanded}
        expandSideBar={expandExpertGroupsSideBar}
      ></ExpertGroupPopup>
      <UpdatesPopup
        isExpanded={isUpdatesExpanded}
        expandSideBar={expandUpdatesSideBar}
      ></UpdatesPopup>
    </>
  );
};

export default ModernApp;
